// Single source of truth for Flora Dine contact details.
// Update here and every component (Footer, Contact page, ChatBot, WhatsApp) stays in sync.

export const CONTACT = {
  // One published mailbox for every kind of enquiry - sales, exports, careers,
  // data protection and support. Splitting it across several addresses meant a
  // buyer who guessed wrong bounced (audit T4); a single address cannot be
  // guessed wrong. Add a new one here only if it is genuinely monitored.
  email: 'support@floradine.com',

  // Display values
  phoneIndia: '+91 6306612700',
  phoneUK: '+44 7438022070',

  // tel: href values (no spaces)
  phoneIndiaTel: '+916306612700',
  phoneUKTel: '+447438022070',

  // WhatsApp click-to-chat numbers  - digits only, no `+` and no spaces.
  // Both published lines are on WhatsApp; see PHONE_LINES below, which is what
  // components should read rather than reaching for these directly.
  whatsappIndia: '916306612700',
  whatsappUK: '447438022070',

  addressLine1: 'S-8/110, Maqbool Alam Rd, Khajuri Colony, Varanasi',
  addressLine2: 'Uttar Pradesh 221002, India',
  mapUrl: 'https://maps.app.goo.gl/6AqWoLfyK1M5gdrU9',

  // Single source of truth for the response-time promise (ISS-17). Reuse this
  // string everywhere the SLA is surfaced (contact page, quote page, form
  // success states, metadata) so the wording stays identical site-wide.
  responsePromise: 'We respond within 24–48 hours on business days',

  // Registered legal entity. UK/EU buyers run registry checks before they
  // shortlist a supplier, so the entity name is displayed in the footer
  // (audit T5). CIN / GST / IEC numbers are owed by the business  - add them to
  // `registrations` below and the footer renders them automatically.
  legalEntity: 'Flora Dine Private Limited',
  registrations: [] as { label: string; value: string }[],

  // Office hours. Always publish the timezone  - a UK buyer cannot work out when
  // to call otherwise (audit T8).
  officeHours: {
    timezone: 'IST (GMT+5:30)',
    weekdays: 'Monday – Friday, 9:00 – 17:00 IST',
    saturday: 'Saturday: by appointment',
  },

  // Public social profiles - real URLs only. Add LinkedIn/Twitter/YouTube here
  // once live and they render automatically in the footer + contact page.
  socials: [
    { name: 'YouTube', icon: 'Youtube', href: 'https://www.youtube.com/@Floradine-f8m/featured' },
    { name: 'Instagram', icon: 'Instagram', href: 'https://www.instagram.com/floradine_' },
    {
      name: 'Facebook',
      icon: 'Facebook',
      href: 'https://www.facebook.com/profile.php?id=61590765928070',
    },
  ],
} as const;

/**
 * Published email routing (audit T4). The site once advertised five different
 * addresses with no guidance on which to use, so a buyer who guessed wrong
 * bounced. There is now one monitored mailbox and it takes everything.
 */
export const EMAIL_ROUTING = [
  {
    purpose: 'Every enquiry',
    email: CONTACT.email,
    detail:
      'Quotes, pricing, MOQs, samples, export documentation, existing orders, careers and data-protection requests.',
  },
] as const;

export interface PhoneLine {
  /** Which market the line serves, e.g. 'UK'. Used in labels and aria-labels. */
  region: string;
  /** Human-readable number, with spaces. */
  display: string;
  /** `tel:` href value  - no spaces. */
  tel: string;
  /**
   * WhatsApp click-to-chat number, digits only. `null` means the line is not
   * registered on WhatsApp, and no WhatsApp affordance is rendered for it -
   * `wa.me` serves an "invalid number" error page for unregistered numbers,
   * which is a worse experience than offering no button at all.
   */
  wa: string | null;
}

/** The two published phone lines, each wired to its own `tel:` href (audit F7). */
export const PHONE_LINES: PhoneLine[] = [
  {
    region: 'UK',
    display: CONTACT.phoneUK,
    tel: CONTACT.phoneUKTel,
    wa: CONTACT.whatsappUK,
  },
  {
    region: 'India',
    display: CONTACT.phoneIndia,
    tel: CONTACT.phoneIndiaTel,
    wa: CONTACT.whatsappIndia,
  },
];

/**
 * WhatsApp click-to-chat URL. `wa.me` is the official short form.
 *
 * `text` is prefilled into the buyer's compose box, so vary it per surface -
 * a message that arrives saying "UK enquiry" is worth more to sales than a
 * generic one, and it is the only attribution available once the buyer has
 * left the site.
 */
export const waUrl = (wa: string, text: string) =>
  // Strip non-digits defensively: the `+`-prefixed tel: constants sit right
  // beside the WhatsApp ones, and wa.me silently 404s on a number containing
  // a `+` or a space rather than failing loudly.
  `https://wa.me/${wa.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
